import { useSelector } from "store/useSelector";
import { isEqual } from "lodash";
import { useHubletViewCurrentRatePerMs } from "utils/customHooks/genericHooks";
import {
  useLotInfoForProcessId,
  useProcessIdOnSingleProcessPage,
} from "./lotInfoUtils";
import { getTargetRatePerMs } from "components/Dashboard/HubletView/hubletViewUtils";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { CUSTOMERS_LOT_INFO_SCALED_TARGET_RATE } from "constants/customers";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";

/** calculate the date (ms) when the current lot should end
 *
 * a return value of Infinity indicates that the count/rate is still 0
 *
 * a return value of null indicates that we're missing some
 * data required for the calculation (e.g. lot size)
 */
export function useForecastedLotCompletionDateMs(): number | null {
  const processId = useProcessIdOnSingleProcessPage();
  const lotInfoForProcess = useLotInfoForProcessId(processId);
  const lotSize = lotInfoForProcess?.lotSize;
  const currentCount = useSelector(
    state => (processId ? state.legacy.allLotCounts[processId] : null),
    isEqual
  );
  const targetRatePerMs = useTargetRateForProcess(processId);
  const currentRatePerMs = useHubletViewCurrentRatePerMs();

  const [
    {
      general: { lotInfoModuleUseTargetRateInsteadOfCurrentRate },
    },
  ] = useFlightConfig();
  const ratePerMs = lotInfoModuleUseTargetRateInsteadOfCurrentRate
    ? targetRatePerMs
    : currentRatePerMs;

  // count and rate of 0 are valid values, the date is Infinity i.e. "can't predict"

  // if we don't have count, can't predict a completion date
  if (!currentCount) {
    return Infinity;
  }

  const partsRemaining = lotSize - currentCount;

  // if negative parts remaining, the lot should be completed already
  if (partsRemaining < 0) {
    return -Infinity; // we use this value to show the "please complete the lot" warning banner
  }

  // if we have count, but rate is 0, can't predict a completion date
  if (ratePerMs === 0) {
    return Infinity;
  }

  // if we don't have rate, lot size, or count, show "--" in the lot info bar
  if (!ratePerMs || !lotSize || !currentCount) {
    return null;
  }

  const timeRemaining = partsRemaining / ratePerMs;

  return timeRemaining ? Date.now() + timeRemaining : null;
}

function useTargetRateForProcess(processId: string | null) {
  const ckey = useCustomerKey();
  const dataDump = useSelector(state => state.legacy.dataDumpHubletView);
  if (!processId) {
    return null;
  }
  const targetRatePerMS = getTargetRatePerMs(
    dataDump?.lastSegment?.targetRate,
    dataDump?.lastSegment?.rateDenominator
  );

  const targetRateScale = CUSTOMERS_LOT_INFO_SCALED_TARGET_RATE?.[ckey] ?? 1;
  const scaledTargetRateForLotProgression = targetRatePerMS
    ? targetRatePerMS * targetRateScale
    : targetRatePerMS;

  return scaledTargetRateForLotProgression;
}
